// FIN-11802 (Phase 4.4 GREEN) — modal opened by the Launch-gate saga
// (FIN-11800) when /scenarios/:id/validate returns a non-empty errors
// list. The saga dispatches SHOW_VALIDATION_ERRORS({scenarioID, errors});
// configureScenario consumes that and renders this modal. OK closes the
// modal (dispatch hideValidationErrors); the user is returned to the
// Configure view to fix the offending variants one at a time (the
// per-variant save path from FIN-11794).
import React from 'react';
import PropTypes from 'prop-types';
import { Modal, Table } from 'antd';
import { stripTypologyPrefix } from '../../../util/typology';

// FIN-11922 / FIN-12130: AFC-uploaded typology names carry a literal "Typology"
// prefix. stripTypologyPrefix (shared util) removes it for display — same
// helper used by the Explanation Score Computation table.

const columns = [
  {
    title: 'Typology',
    dataIndex: 'typology',
    key: 'typology',
    width: 230
  },
  {
    title: 'Variant',
    dataIndex: 'ruleName',
    key: 'ruleName',
    width: 230
  },
  {
    title: 'Rule',
    dataIndex: 'ruleId',
    key: 'ruleId',
    width: 180
  },
  {
    title: 'Error',
    dataIndex: 'message',
    key: 'message',
    width: 420
  }
];

// Flatten { typologyId, ruleId, ruleName, messages:[...] } rows into one
// AntD Table row per individual error message so the user sees every
// failure plainly. Errors with no messages still render one row with
// "(no message)" to flag them.
function flatten(errors) {
  const out = [];
  (errors || []).forEach((e, idx) => {
    const messages = e.messages && e.messages.length > 0 ? e.messages : [''];
    const typologyName = stripTypologyPrefix(e.typologyName || '');
    // "name (id)" — keep the id for traceability; fall back to id alone.
    const typology = typologyName
      ? `${typologyName} (${e.typologyId})`
      : `${e.typologyId}`;
    messages.forEach((msg, midx) => {
      out.push({
        key: `${idx}-${midx}`,
        typology,
        ruleId: e.ruleId,
        ruleName: stripTypologyPrefix(e.ruleName || ''),
        message: msg || '(no message)'
      });
    });
  });
  return out;
}

const warningColumns = columns.map((c) =>
  c.key === 'message' ? { ...c, title: 'Warning' } : c
);

const ScenarioValidationErrorModal = ({ visible, errors, warnings, onOk }) => (
  <Modal
    title="Scenario validation failed"
    visible={visible}
    onOk={onOk}
    onCancel={onOk}
    okText="OK"
    cancelButtonProps={{ style: { display: 'none' } }}
    // FIN-11922: wider modal so the Typology/Variant names don't wrap into tall
    // cells. Capped responsively so it never exceeds the viewport.
    width="90vw"
    style={{ maxWidth: 1100 }}
    destroyOnClose
  >
    <p>
      The following rules must be fixed before this scenario can be launched.
      Open each variant in turn from the Configure view to correct it.
    </p>
    <Table
      columns={columns}
      dataSource={flatten(errors)}
      pagination={false}
      size="small"
      // Vertical scroll for many rows; horizontal scroll kicks in only when the
      // combined column width (1060) exceeds the modal (narrow screens / long
      // single-line error text such as the grouping-keys JSON).
      scroll={{ x: 1060, y: 440 }}
    />
    {/* FIN-13484: advisory findings. They do not block launch; they are listed here so the
        author sees them next to the errors they are fixing anyway. */}
    {warnings && warnings.length > 0 && (
      <div data-testid="validation-warnings-section" style={{ marginTop: 16 }}>
        <p>
          Advisory warnings — these do not block launch, but the affected rule
          may never match until it is corrected.
        </p>
        <Table
          columns={warningColumns}
          dataSource={flatten(warnings)}
          pagination={false}
          size="small"
          scroll={{ x: 1060, y: 240 }}
        />
      </div>
    )}
  </Modal>
);

const findingShape = PropTypes.arrayOf(
  PropTypes.shape({
    typologyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    typologyName: PropTypes.string,
    ruleId: PropTypes.string,
    ruleName: PropTypes.string,
    messages: PropTypes.arrayOf(PropTypes.string)
  })
);

ScenarioValidationErrorModal.propTypes = {
  visible: PropTypes.bool,
  warnings: findingShape,
  errors: PropTypes.arrayOf(
    PropTypes.shape({
      typologyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      typologyName: PropTypes.string,
      ruleId: PropTypes.string,
      ruleName: PropTypes.string,
      messages: PropTypes.arrayOf(PropTypes.string)
    })
  ),
  onOk: PropTypes.func.isRequired
};

ScenarioValidationErrorModal.defaultProps = {
  visible: false,
  errors: [],
  warnings: []
};

export default ScenarioValidationErrorModal;
